<!-- 节点查询：支持按节点名称、节点 ID 查询画布内的节点 -->

<template>
  <div ref="rootRef" class="node-search" @keydown="handleKeydown">
    <div @click="handleFocus">
      <t-input
        v-model="keyword"
        :placeholder="$t('请输入节点标签或者ID 搜索')"
        clearable
        @focus="handleFocus"
        @change="handleInput"
        @clear="handleClear"
      >
        <template #prefix-icon>
          <SearchIcon />
        </template>
      </t-input>
    </div>

    <div
      v-if="panelVisible"
      :class="['result-panel', `placement-${placement}`]"
    >
      <template v-if="results.length">
        <div
          v-for="(item, index) in results"
          :key="item.id"
          :class="[
            'result-item',
            { active: index === activeIndex, selected: item.id === selectedId },
          ]"
          @mouseenter="activeIndex = index"
          @click="handleSelect(item)"
        >
          <img
            v-if="item.icon"
            class="result-icon"
            :src="item.icon"
            :alt="item.typeName"
          />
          <div class="result-info">
            <div class="result-name">
              <span
                v-for="(seg, segIndex) in item.nameSegments"
                :key="segIndex"
                :class="{ matched: seg.matched }"
                >{{ seg.text }}</span
              >
            </div>
            <div v-if="item.typeName" class="result-desc">
              {{ item.typeName }}
            </div>
          </div>
        </div>
      </template>
      <div v-else class="result-empty">{{ $t("暂无匹配节点") }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { SearchIcon } from "tdesign-icons-vue-next";
import { debounce, get, trim } from "lodash-es";

type NodeCfg = {
  type: string;
  name: string;
  icon: string;
};

type TextSegment = {
  text: string;
  matched: boolean;
};

type NodeItem = {
  id: string;
  name: string;
  typeName: string;
  icon: string;
};

type SearchResult = NodeItem & {
  nameSegments: TextSegment[];
};

const props = withDefaults(
  defineProps<{
    graph?: any;
    nodeList?: NodeCfg[];
    // 结果列表展开方向，放在底部工具栏时需要向上展开
    placement?: "top" | "bottom";
  }>(),
  {
    graph: null,
    nodeList: () => [],
    placement: "bottom",
  },
);

const emit = defineEmits<{
  (e: "select", nodeId: string): void;
}>();

// 结果列表最多展示的条数
const MAX_RESULT_COUNT = 50;
// 画布变动后需要重新采集节点，这些事件用于触发重新采集
const GRAPH_EVENTS = [
  "afterrender",
  "afteradditem",
  "afterremoveitem",
  "afterupdateitem",
];

const rootRef = ref<HTMLElement | null>(null);
const keyword = ref("");
const panelVisible = ref(false);
const activeIndex = ref(0);
const allNodes = ref<NodeItem[]>([]);
// 已选中的节点，输入框回显它的标签
const selectedId = ref("");
// 是否处于输入筛选状态。回显选中节点时不按输入框内容过滤，避免只剩选中的那一条
const isFiltering = ref(false);

// 选中的节点被改名或删除时，同步输入框里的回显内容
const syncSelectedKeyword = () => {
  if (!selectedId.value || isFiltering.value) return;

  const node = allNodes.value.find((v) => v.id === selectedId.value);
  keyword.value = node?.name ?? "";
  if (!node) {
    selectedId.value = "";
  }
};

const refreshNodes = () => {
  const graph = props.graph;
  if (!graph) {
    allNodes.value = [];
    return;
  }

  allNodes.value = graph.getNodes().map((node: any) => {
    const model = node.getModel();
    const nodeType = get(model, "nodeData.nodeType", "");
    const nodeCfg = props.nodeList.find((v) => v.type === nodeType);
    // label 是画布上折行展示的文案，兜底时需要去掉换行
    const label = String(model.label ?? "").replace(/\n/g, "");
    return {
      id: String(model.id ?? ""),
      name: String(get(model, "nodeData.nodeName", "") || label),
      typeName: nodeCfg?.name ?? "",
      icon: nodeCfg?.icon ?? "",
    };
  });

  syncSelectedKeyword();
};

// 拖拽节点会高频触发 afterupdateitem，这里做一次节流
const debouncedRefreshNodes = debounce(refreshNodes, 150);

// 把文本按关键字切成若干片段，用于高亮命中部分
const splitByKeyword = (text: string, key: string): TextSegment[] => {
  if (!key) return [{ text, matched: false }];

  const segments: TextSegment[] = [];
  const lowerText = text.toLowerCase();
  let start = 0;
  let index = lowerText.indexOf(key, start);

  while (index !== -1) {
    if (index > start) {
      segments.push({ text: text.slice(start, index), matched: false });
    }
    segments.push({
      text: text.slice(index, index + key.length),
      matched: true,
    });
    start = index + key.length;
    index = lowerText.indexOf(key, start);
  }
  if (start < text.length) {
    segments.push({ text: text.slice(start), matched: false });
  }
  return segments;
};

const results = computed<SearchResult[]>(() => {
  // 输入框回显选中节点时展示全部节点，只有用户主动输入才过滤
  const key = isFiltering.value ? trim(keyword.value).toLowerCase() : "";

  const matched = key
    ? allNodes.value.filter(
        (node) =>
          node.name.toLowerCase().includes(key) ||
          node.id.toLowerCase().includes(key),
      )
    : allNodes.value;

  // 名称前缀命中 > 名称命中 > 仅 ID 命中
  const getWeight = (node: { name: string; id: string }) => {
    const name = node.name.toLowerCase();
    if (!key) return 0;
    if (name.startsWith(key)) return 0;
    if (name.includes(key)) return 1;
    return 2;
  };

  return matched
    .slice()
    .sort((a, b) => getWeight(a) - getWeight(b))
    .slice(0, MAX_RESULT_COUNT)
    .map((node) => ({
      ...node,
      nameSegments: splitByKeyword(node.name, key),
    }));
});

watch(results, (list) => {
  // 未输入筛选时，默认高亮已选中的那条
  const index = isFiltering.value
    ? -1
    : list.findIndex((v) => v.id === selectedId.value);
  activeIndex.value = index > -1 ? index : 0;
});

const scrollActiveItemIntoView = async () => {
  await nextTick();
  const activeItem = rootRef.value?.querySelector(".result-item.active");
  activeItem?.scrollIntoView({ block: "nearest" });
};

const handleFocus = () => {
  refreshNodes();
  panelVisible.value = true;
};

const handleInput = () => {
  isFiltering.value = true;
};

const handleClear = () => {
  isFiltering.value = true;
  selectedId.value = "";
};

const handleSelect = (item: SearchResult) => {
  selectedId.value = item.id;
  isFiltering.value = false;
  // 输入框回显选中的节点标签
  keyword.value = item.name;
  panelVisible.value = false;
  emit("select", item.id);
};

const moveActiveIndex = (step: number) => {
  const total = results.value.length;
  if (!total) return;
  activeIndex.value = (activeIndex.value + step + total) % total;
  scrollActiveItemIntoView();
};

const handleKeydown = (e: KeyboardEvent) => {
  if (!panelVisible.value) return;

  if (e.key === "ArrowDown") {
    e.preventDefault();
    moveActiveIndex(1);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    moveActiveIndex(-1);
  } else if (e.key === "Enter") {
    e.preventDefault();
    const item = results.value[activeIndex.value];
    if (item) handleSelect(item);
  } else if (e.key === "Escape") {
    panelVisible.value = false;
  }
};

const handleClickOutside = (e: MouseEvent) => {
  if (!rootRef.value?.contains(e.target as Node)) {
    panelVisible.value = false;
  }
};

const bindGraphEvents = (graph: any) => {
  GRAPH_EVENTS.forEach((name) => graph.on(name, debouncedRefreshNodes));
};

const unbindGraphEvents = (graph: any) => {
  GRAPH_EVENTS.forEach((name) => graph.off(name, debouncedRefreshNodes));
};

// graph 在父组件 mounted 后才创建，需要等它就绪再绑定事件
watch(
  () => props.graph,
  (graph, oldGraph) => {
    if (oldGraph) unbindGraphEvents(oldGraph);
    if (graph) {
      bindGraphEvents(graph);
      refreshNodes();
    }
  },
  { immediate: true },
);

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  debouncedRefreshNodes.cancel();
  if (props.graph) unbindGraphEvents(props.graph);
});
</script>

<style lang="less" scoped>
.node-search {
  position: relative;
  width: 100%;
}

.result-panel {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  &.placement-bottom {
    top: calc(100% + 6px);
  }
  &.placement-top {
    bottom: calc(100% + 6px);
  }
  max-height: 300px;
  overflow-y: auto;
  padding: 6px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow:
    0px 4px 10px rgba(32, 77, 141, 0.08),
    0px 1px 2px rgba(32, 77, 141, 0.08);
}

.result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  &.active {
    background-color: #f3f6fb;
  }
  &.selected .result-name {
    color: #1f69e5;
  }
}

.result-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.result-info {
  min-width: 0;
  flex: 1;
}

.result-name {
  font-size: 14px;
  line-height: 22px;
  color: #232b3a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-desc {
  font-size: 12px;
  line-height: 20px;
  color: #6d778c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.matched {
  color: #1f69e5;
}

.result-empty {
  padding: 12px 8px;
  font-size: 14px;
  color: #6d778c;
  text-align: center;
}
</style>
